import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { MessageCircle, ShieldAlert, Send, CheckCircle2, XCircle, Sparkles, Hash } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";

export default function AdminDiscord() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.get("/admin/discord/status").then((r) => setData(r.data)).catch(() => {});
  }, []);

  useEffect(() => { load(); }, [load]);

  const promptNow = async () => {
    setBusy(true);
    try {
      const r = await api.post("/admin/discord/prompt-now");
      toast.success(`Posted: ${r.data.prompt}`);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  if (!data) return <p className="font-mono text-[10px] text-zinc-600 animate-pulse p-4">Loading Discord community status…</p>;

  return (
    <section data-testid="admin-discord-section" className="border border-white/10 bg-black/40">
      <div className="border-b border-white/10 px-6 py-4">
        <h2 className="font-sans font-semibold uppercase tracking-widest text-base text-zinc-100 flex items-center gap-2">
          <MessageCircle className="w-4 h-4 text-nc-blue" /> NDIS Community Discord
        </h2>
        <p className="font-mono text-[10px] text-zinc-500 mt-2 leading-relaxed">
          Private, neuro-affirming student community. The bot auto-greets new students, locks them to safe channels, posts a daily visual prompt at 9am Melbourne time, and flags sensitive content to moderators.
        </p>
      </div>

      <div className="p-6 grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="flex flex-col gap-4">
          <div className="flex items-center gap-2 flex-wrap">
            <span data-testid="discord-config-status" className={`font-mono text-[9px] tracking-[0.2em] uppercase border px-2.5 py-1.5 flex items-center gap-1.5 ${data.configured ? "text-nc-green border-nc-green/40 bg-nc-green/5" : "text-red-400 border-red-500/40 bg-red-500/5"}`}>
              {data.configured ? <CheckCircle2 className="w-3 h-3" /> : <XCircle className="w-3 h-3" />} Keys: {data.configured ? "set" : "missing"}
            </span>
            <span data-testid="discord-online-status" className={`font-mono text-[9px] tracking-[0.2em] uppercase border px-2.5 py-1.5 ${data.online ? "text-nc-green border-nc-green/40 bg-nc-green/5" : "text-zinc-500 border-white/15"}`}>
              Bot: {data.online ? `online — ${data.guild_name} (${data.member_count} members)` : "offline"}
            </span>
          </div>

          {!data.configured && (
            <div data-testid="discord-setup-guide" className="border border-nc-orange/40 bg-nc-orange/5 p-4">
              <p className="font-mono text-[10px] tracking-[0.2em] text-nc-orange uppercase mb-2">One-time setup — about 10 minutes</p>
              <ol className="font-mono text-[10px] text-zinc-400 leading-relaxed list-decimal list-inside flex flex-col gap-1.5">
                <li>Create an app at <b className="text-zinc-200">discord.com/developers</b> → New Application → Bot tab → copy the token</li>
                <li>Enable privileged intents: <b className="text-zinc-200">Server Members</b> + <b className="text-zinc-200">Message Content</b></li>
                <li>Create your private Discord server, then invite the bot (OAuth2 → URL Generator → scope <b className="text-zinc-200">bot</b>, permissions: Manage Channels, Manage Roles, Send Messages, Manage Messages)</li>
                <li>Copy your server ID (right-click server name → Copy Server ID, needs Developer Mode on)</li>
                <li>Send the dev team <b className="text-zinc-200">DISCORD_BOT_TOKEN</b> and <b className="text-zinc-200">DISCORD_GUILD_ID</b> — the bot builds the entire channel layout itself on first connect</li>
              </ol>
              <p className="font-mono text-[9px] text-zinc-600 mt-3">Safeguarding: participants under 18 should use parent-visible accounts; mentors working with minors need working-with-children clearance.</p>
            </div>
          )}

          <div className="border border-white/10 bg-[#050505]/50 p-4">
            <p className="font-mono text-[9px] tracking-[0.25em] text-nc-blue uppercase mb-3">Community layout (auto-built by the bot)</p>
            {data.channel_plan.map((c) => (
              <div key={c.name} className="flex items-start gap-2 py-1.5 border-b border-white/5">
                <Hash className="w-3 h-3 text-zinc-500 mt-0.5 shrink-0" />
                <div>
                  <p className="font-mono text-[11px] text-zinc-200">{c.name} <span className="text-zinc-600">· {c.kind === "read_only" ? "read-only" : c.kind === "mods_only" ? "moderators only" : "chat + slow mode"}</span></p>
                  <p className="font-mono text-[9px] text-zinc-500">{c.topic}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="border border-white/10 bg-[#050505]/50 p-4 flex items-center justify-between gap-3 flex-wrap">
            <div>
              <p className="font-mono text-[9px] tracking-[0.25em] text-nc-orange uppercase flex items-center gap-2"><Sparkles className="w-3 h-3" /> Today's prompt</p>
              <p data-testid="discord-next-prompt" className="font-mono text-[11px] text-zinc-300 mt-1.5">{data.next_prompt}</p>
            </div>
            <button data-testid="discord-prompt-now" disabled={busy || !data.online} onClick={promptNow} className="bg-nc-blue hover:bg-nc-blue/80 disabled:opacity-40 text-white font-sans font-semibold uppercase tracking-[0.2em] text-[10px] px-4 py-2.5 flex items-center gap-2 transition-colors">
              <Send className="w-3.5 h-3.5" /> Post Now
            </button>
          </div>
        </div>

        <div className="border border-white/10 bg-[#050505]/50 p-4 flex flex-col gap-2">
          <p className="font-mono text-[9px] tracking-[0.25em] text-nc-orange uppercase flex items-center gap-2"><ShieldAlert className="w-3.5 h-3.5" /> Moderation Flags</p>
          {data.recent_flags.length === 0 && <p data-testid="discord-flags-empty" className="font-mono text-[10px] text-zinc-600">No flags — the community is quiet and safe.</p>}
          {data.recent_flags.map((f, i) => (
            <div key={f.id} data-testid={`discord-flag-${i + 1}`} className="border-b border-white/5 py-2">
              <div className="flex items-center justify-between gap-2">
                <span className="font-mono text-[10px] text-red-400 uppercase tracking-[0.15em]">{f.kinds.join(", ")}</span>
                <span className="font-mono text-[9px] text-zinc-500">{String(f.created_at).slice(5, 16).replace("T", " ")}</span>
              </div>
              <p className="font-mono text-[10px] text-zinc-400 mt-1">{f.author} in #{f.channel}: {f.excerpt}</p>
              {f.jump_url && <a href={f.jump_url} target="_blank" rel="noopener noreferrer" className="font-mono text-[9px] text-nc-blue hover:underline">Open in Discord</a>}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
